import type { CenterArea } from '../types/center.ts';
import type { GameState } from '../types/game-state.ts';
import type {
  MaskedCenterArea,
  MaskedGameState,
  MaskedHolding,
  MaskedPlayer,
  MaskedTile,
} from '../types/masked-game-state.ts';
import type { Player } from '../types/player.ts';
import type { Holding, Tile } from '../types/tile.ts';

export function maskGameState(state: GameState, viewerId: string): MaskedGameState {
  const players: MaskedPlayer[] = state.players.map((p) => maskPlayer(p));
  const center: MaskedCenterArea = maskCenter(state.center);

  const currentPlayerId = state.players[state.currentPlayerIdx]?.id ?? null;
  const isViewerCurrent = currentPlayerId === viewerId;
  const holding: MaskedHolding | null =
    state.holding === null ? null : maskHolding(state.holding, isViewerCurrent);

  return {
    roomCode: state.roomCode,
    players,
    center,
    currentPlayerIdx: state.currentPlayerIdx,
    bonusActive: state.bonusActive,
    status: state.status,
    winnerId: state.winnerId,
    holding,
    metrics: { ...state.metrics },
    turnDeadline: state.turnDeadline,
  };
}

function maskPlayer(player: Player): MaskedPlayer {
  return {
    id: player.id,
    nickname: player.nickname,
    isHost: player.isHost,
    connected: player.connected,
    lane: {
      slots: player.lane.slots.map((s) => ({ tile: maskTile(s.tile) })),
    },
  };
}

function maskCenter(center: CenterArea): MaskedCenterArea {
  return {
    deckCount: center.deck.length,
    openPool: center.openPool.map((t) => toMaskedTile(t)),
  };
}

function maskTile(tile: Tile): MaskedTile {
  if (tile.face === 'UP') return toMaskedTile(tile);
  return { id: tile.id, number: null, face: 'DOWN', resolvedNumber: null };
}

function maskTileForOthers(tile: Tile): MaskedTile {
  return { id: tile.id, number: null, face: tile.face, resolvedNumber: null };
}

function maskHolding(holding: Holding, isViewerCurrent: boolean): MaskedHolding {
  const base = isViewerCurrent ? toMaskedTile(holding) : maskTileForOthers(holding);
  return { ...base, source: holding.source };
}

function toMaskedTile(tile: Tile): MaskedTile {
  return {
    id: tile.id,
    number: tile.number,
    face: tile.face,
    resolvedNumber: tile.resolvedNumber,
  };
}
